import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { useResponsive } from '@sg/garnish';

import { Address, useAddressHandlers as useAddressHandlersV2 } from '@order/features/locations';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useCustomer } from '../../context/Customer';
import { useCart } from '../../hooks';
import { AddressForm, useAddressHandlers } from '../Address';
import { Modal } from '../Modal';

export const AddressEditModal = (props: AddressEditModalProps) => {
  const { addressId, visible, onRequestClose } = props;

  // ─── Flags ────────────────────────────────────────────────────────────

  const isRadarDeliveryEnabled = useFeatureFlag(
    'cels-4823-enable-radar-delivery-address-predictions',
  );

  // ─────────────────────────────────────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const { match } = useResponsive();
  const { customer } = useCustomer();
  const { cart, isCartEmpty, fetchCart } = useCart();

  const customerAddresses = customer?.addresses ?? [];
  const address = customerAddresses?.find((item) => item.id === addressId);

  const currentBagAddressId = cart?.deliveryOrderDetail?.address?.id;
  const isEditingBagAddress = currentBagAddressId === addressId;
  const shouldUpdateCart = !isCartEmpty && isEditingBagAddress;

  const { isUpdatingAddress, isDeletingAddress, handleOnSave, handleOnEdit, handleOnDelete } =
    useAddressHandlers({
      editingAddressId: addressId,
      checkExistingBag: true,
      onClose: onRequestClose,
    });

  // ─── Callbacks ───────────────────────────────────────────────────────────────────

  const handleOnSuccess = useCallback(() => {
    if (shouldUpdateCart) {
      fetchCart();
    }

    onRequestClose();
  }, [fetchCart, onRequestClose, shouldUpdateCart]);

  // ─────────────────────────────────────────────────────────────────────────────

  const {
    handleOnEdit: handleOnEditV2,
    handleOnDelete: handleOnDeleteV2,
    isUpdatingAddress: isUpdatingAddressV2,
    isDeletingAddress: isDeletingAddressV2,
  } = useAddressHandlersV2({
    onSuccess: handleOnSuccess,
    editingAddressId: addressId,
  });

  return (
    <Modal
      fitHeight
      visible={visible}
      accessibilityLabel={formatMessage(messages.modalHeadline)}
      presentationStyle="pageSheet"
      size={match(['full', 'small'])}
      onRequestClose={onRequestClose}
    >
      <Modal.Header onClose={onRequestClose}>
        <FormattedMessage {...messages.modalHeadline} />
      </Modal.Header>

      {isRadarDeliveryEnabled ? (
        <Address.Form
          visible
          editing
          savedCustomerAddress={address}
          customerAddresses={customerAddresses}
          editingAddressId={addressId}
          onSubmit={handleOnEditV2}
          isLoading={isUpdatingAddressV2}
          onDelete={handleOnDeleteV2}
          isDeleting={isDeletingAddressV2}
        />
      ) : (
        <AddressForm
          visible
          editing
          address={address}
          loading={isUpdatingAddress}
          deleting={isDeletingAddress}
          editingAddressId={addressId}
          submitCta={formatMessage(messages.saveAddress)}
          onSave={handleOnSave}
          onEdit={handleOnEdit}
          onDelete={handleOnDelete}
        />
      )}
    </Modal>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────────

const messages = defineMessages({
  saveAddress: {
    defaultMessage: 'Save',
    description: 'Account | Edit Address Form | Save',
  },
  modalHeadline: {
    defaultMessage: 'Edit Address',
    description: 'Account | Modal header | Edit Address',
  },
});

// ─── Types ─────────────────────────────────────────────────────────────

type AddressEditModalProps = Readonly<{
  addressId: string;
  visible: boolean;
  onRequestClose: () => void;
}>;
